<template>
  <main class="detail-page">
    <AppNavBar :title="t('card.benefit')" />

    <section class="benefit-overview">
      <van-skeleton v-if="loading" title :row="4" />
      <template v-else>
        <div class="benefit-overview__bg" :style="overviewStyle">
          <div class="benefit-overview__head">
            <div data-strong>{{ t("card.balanceOverview") }}</div>
            <span v-if="withdrawNotice && !isVirtualCard">
              {{ withdrawNotice }}
            </span>
          </div>

          <div class="benefit-overview__metrics">
            <div
              v-for="item in summaryItems"
              :key="item.key"
              class="metric-item"
            >
              <span>{{ item.label }}</span>
              <div data-strong>{{ item.value }}</div>
            </div>
          </div>

          <div class="limit-box">
            <div class="limit-box__label">
              <app-image :src="monthLimitIcon" alt="" />
              <span>{{ t("card.monthlyCardUsageLimit") }}</span>
            </div>
            <div data-strong>{{ limitText }}</div>
          </div>
        </div>
      </template>
    </section>

    <section class="content-card">
      <h2>{{ t("card.supportedConsumptionScenes") }}</h2>
      <van-skeleton v-if="loading" title :row="5" />
      <AppEmptyState
        v-else-if="!hasDetailContent"
        :description="t('card.noBenefit')"
      />
      <template v-else>
        <div class="scene-panel">
          <div class="scene-group">
            <h3>{{ t("card.keySupportedPlatforms") }}</h3>
            <div class="scene-row">
              <i />
              <p>{{ platforms || "--" }}</p>
            </div>
          </div>

          <div class="scene-divider" />

          <div class="scene-group">
            <h3>{{ t("card.otherPlatforms") }}</h3>
            <div
              v-for="(item, index) in otherRows"
              :key="`${index}-${item}`"
              class="scene-row"
            >
              <i />
              <p>{{ item }}</p>
            </div>
          </div>
        </div>
      </template>
    </section>

    <!-- <section class="content-card">
      <h2>{{ t("card.cardUsageRules") }}</h2>
      <div v-for="(item, index) in ruleRows" :key="item" class="rule-row">
        <em>{{ String(index + 1).padStart(2, "0") }}</em>
        <p>{{ item }}</p>
      </div>
    </section> -->

    <section class="content-card">
      <h2>{{ t("card.cardSafetyTips") }}</h2>
      <div v-for="item in safetyRows" :key="item" class="tip-row">
        <i />
        <p>{{ item }}</p>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { pcCardRightInfo } from "@/api/card";
import { useProfileStore } from "@/stores/profile";
import { getRouteState, navigateWithState } from "@/utils/route-state";
import carBenefitBg from "@/assets/images/cardImage/carBenefit_bg.png";
import monthLimitIcon from "@/assets/images/cardImage/mounthLimit_icon.png";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const info = ref({});
const loading = ref(false);
const pageState = computed(() => getRouteState(route, "card-detail", {}));
const isVirtualCard = computed(() => Number(pageState.value.cardType) === 2);
const overviewStyle = {
  backgroundImage: `url(${carBenefitBg})`,
};

function formatMixedWrapText(text) {
  return String(text || "")
    .replace(/([\u4E00-\u9FFF0-9])(?=[A-Za-z])/g, "$1\u2060")
    .replace(/(?<=[A-Za-z0-9]) (?=[A-Za-z0-9])/g, "\u00A0");
}

function formatOthersItem(text, index) {
  return String(text || "").replace(/^支持(?=[A-Za-z])/, "支\u2060持\u2060");
}

function feeString(value) {
  const number = Number(value || 0);
  const percent = number * 100;
  if (!Number.isFinite(percent)) {
    return "0%";
  }
  return `${Number.isInteger(percent) ? percent : percent}%`;
}

const summaryItems = computed(() => {
  const source = info.value || {};
  return [
    { key: "monthlyFee", label: t("card.monthlyFee"), value: "0.00 USD" },
    {
      key: "feeCharge",
      label: t("card.chargeFeeLabel"),
      value: feeString(source.feeCharge),
    },
    {
      key: "feePay",
      label: t("card.payFeeLabel"),
      value: feeString(source.feePay),
    },
    {
      key: "feeWithdraw",
      label: t("card.withdrawFeeLabel"),
      value: feeString(source.feeWithdraw),
    },
  ];
});

const limitText = computed(() => {
  const limit = info.value?.consumptionLimit;
  const symbol = info.value?.consumptionLimitSymbol || "";
  const unit = info.value?.unit || "";
  if (limit === undefined || limit === null || limit === "") {
    return "--";
  }
  return `${limit}${unit} ${symbol}`.trim();
});

const withdrawNotice = computed(() => {
  const amount = info.value?.withdrawMin;
  if (!amount) {
    return "";
  }
  return t("card.withdrawNotice", {
    amount,
    symbol: info.value?.consumptionLimitSymbol || "",
  });
});

const platforms = computed(() =>
  // return "Apple pay，Google pay，Alipay， 等金融移动支付平台。";
  formatMixedWrapText(String(info.value?.platforms || "").trim()),
);

const otherRows = computed(() => {
  const rows = info.value?.others_arr;
  return Array.isArray(rows)
    ? rows.filter(Boolean).map((item, index) => formatOthersItem(item, index))
    : [];
});

// const ruleRows = computed(() => [
//   t("card.usageRule1"),
//   t("card.usageRule2"),
//   t("card.usageRule3"),
// ]);

const safetyRows = computed(() => [
  t("card.usageRule1"),
  t("card.cardSafetyTip1"),
  t("card.cardSafetyTip2"),
]);

const hasDetailContent = computed(() =>
  Boolean(platforms.value || otherRows.value.length),
);

const openUnbindPage = () => {
  navigateWithState(router, "/card/unbind/info", {
    namespace: "card-unbind-info",
    payload: pageState.value,
  });
};

async function loadInfo() {
  if (!pageState.value.cardId) return;
  loading.value = true;
  try {
    info.value = await pcCardRightInfo({
      address: profileStore.platformAddress || null,
      cardId: pageState.value.cardId,
    });
  } catch (error) {
    info.value = {};
  } finally {
    loading.value = false;
  }
}

onMounted(loadInfo);
</script>

<style scoped lang="scss">
.detail-page {
  min-height: var(--app-viewport-height);
  padding: 15px 14px 24px;
  background: #f5f5f5;
}

.benefit-overview {
  margin-top: 12px;
}

.benefit-overview__bg {
  padding: 16px;
  border-radius: 16px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.benefit-overview__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  [data-strong] {
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
  }

  span {
    max-width: 148px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    text-align: right;
  }
}

.benefit-overview__metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 24px;
}

.metric-item {
  position: relative;
  padding: 0 6px;
}

.metric-item + .metric-item::before {
  position: absolute;
  top: 2px;
  left: 0;
  width: 1px;
  height: 35px;
  background: #9ec3ff;
  content: "";
}

.metric-item span {
  display: block;
  color: #ffffff;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}

.metric-item [data-strong] {
  display: block;
  margin-top: 4px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

.limit-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding: 9px 11px;
  border: 1px solid #ffffff;
  border-radius: 8px;
  background: linear-gradient(136deg, #d5e4ff 20.71%, #ffffff 87.32%);
}

.limit-box__label {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  .app-image {
    width: 18px;
    height: 18px;
  }

  span {
    color: #333333;
    font-size: 14px;
    line-height: 20px;
  }
}

.limit-box [data-strong] {
  color: var(--wp-color-theme);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: right;
}

.content-card {
  margin-top: 10px;
  padding: 16px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);

  h2 {
    margin: 0;
    color: #000000;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
  }
}

.scene-panel {
  margin-top: 10px;
  padding: 16px;
  border: 1px solid #ededed;
  border-radius: 16px;
}

.scene-group h3 {
  margin: 0;
  color: var(--wp-color-theme);
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
}

.scene-divider {
  height: 1px;
  margin: 16px 0;
  background: #f4f5f6;
}

.scene-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-top: 10px;

  i {
    flex: 0 0 5px;
    width: 5px;
    height: 5px;
    margin-top: 9px;
    border-radius: 50%;
    background: var(--wp-color-theme);
  }

  p {
    margin: 0;
    color: #333333;
    font-size: 14px;
    line-height: 22px;
    word-break: break-word;
  }
}

.rule-row {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  padding-top: 16px;
  font-weight: 500;

  em {
    min-width: 28px;
    padding: 2px 0;
    border-radius: 4px;
    background: linear-gradient(90deg, #4878ee 0.87%, #6ea2f9 99.27%);
    color: #ffffff;
    font-size: 12px;
    font-style: normal;
    line-height: 16px;
    text-align: center;
  }

  p {
    flex: 1;
    margin: 0;
    color: #333333;
    font-size: 14px;
    line-height: 22px;
    word-break: break-word;
  }
}

.tip-row {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 16px;
  padding: 8px 16px 8px 10px;
  border-radius: 10px;
  background: #fff8ed;
  font-weight: 500;

  i {
    flex: 0 0 7px;
    width: 7px;
    height: 7px;
    margin-top: 6px;
    border-radius: 50%;
    background: #e88422;
  }

  p {
    margin: 0;
    color: #333333;
    font-size: 14px;
    line-height: 22px;
    word-break: break-word;
  }
}

.unbind-btn {
  width: 100%;
  margin-top: 20px;
  padding: 12px 16px;
  border: 0;
  border-radius: 30px;
  background: #ef0027;
  color: #ffffff;
  font-size: 16px;
  line-height: 22px;
}
</style>
